// React Router supports one blocker. Every draft in this router shares it.
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type BlockerFunction, useBlocker } from "react-router";
import { recordAddressesIn } from "../app/records";

type Blocker = ReturnType<typeof useBlocker>;
export interface DraftOrigin {
  readonly pathname: string;
  readonly records: readonly string[];
  /** Only these query values can unmount this form's owner. */
  readonly query?: Readonly<Record<string, readonly string[]>>;
}
interface Draft extends DraftOrigin {
  readonly id: symbol;
}
interface GuardScope {
  readonly blocker: Blocker;
  readonly owner: symbol | null;
  readonly register: (id: symbol, origin: DraftOrigin) => () => void;
  readonly discard: () => void;
  readonly keep: () => void;
}
const Scope = createContext<GuardScope | null>(null);

/** A nested application/test composition uses the existing router's one scope. */
export function DiscardGuardScope({ children }: { readonly children: ReactNode }) {
  return useContext(Scope) ? children : <GuardHost>{children}</GuardHost>;
}

export function useDiscardGuardScope(): GuardScope {
  const scope = useContext(Scope);
  if (!scope)
    throw new Error("Draft navigation requires DiscardGuardScope inside the data router.");
  return scope;
}

function losesDraft(
  draft: DraftOrigin,
  next: Parameters<BlockerFunction>[0]["nextLocation"],
): boolean {
  if (draft.pathname !== next.pathname) return true;
  const params = new URLSearchParams(next.search);
  if (
    Object.entries(draft.query ?? {}).some(([key, held]) => {
      const values = params.getAll(key);
      return values.length !== held.length || values.some((value, index) => value !== held[index]);
    })
  )
    return true;
  const records = recordAddressesIn(next.state);
  // A query edit or added quick view retains the owner; a removed/replaced parent does not.
  return draft.records.some((address, index) => records[index] !== address);
}

function GuardHost({ children }: { readonly children: ReactNode }) {
  const [guards, setGuards] = useState<readonly Draft[]>([]);
  const live = useRef<readonly Draft[]>([]);
  const register = useCallback((id: symbol, origin: DraftOrigin) => {
    live.current = [...live.current, { id, ...origin }];
    setGuards(live.current);
    return () => {
      live.current = live.current.filter((guard) => guard.id !== id);
      setGuards(live.current);
    };
  }, []);
  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ nextLocation }) => live.current.some((draft) => losesDraft(draft, nextLocation)),
      [],
    ),
  );
  const answered = useRef<Blocker | null>(null);
  const discard = useCallback(() => {
    if (blocker.state !== "blocked" || answered.current === blocker) return;
    answered.current = blocker;
    blocker.proceed();
  }, [blocker]);
  const keep = useCallback(() => {
    if (blocker.state !== "blocked" || answered.current === blocker) return;
    answered.current = blocker;
    blocker.reset();
  }, [blocker]);

  const blockedOwner =
    blocker.state === "blocked"
      ? (guards.filter((draft) => losesDraft(draft, blocker.location)).at(-1)?.id ?? null)
      : null;
  // Saving one form cannot authorize losing another. Transfer the one question to
  // the latest remaining affected form; proceed only when no affected draft remains.
  useEffect(() => {
    if (blocker.state === "blocked" && blockedOwner === null) discard();
  }, [blocker, blockedOwner, discard]);
  const dirty = guards.length > 0;
  useEffect(() => {
    if (!dirty) return;
    const beforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty]);

  const scope = useMemo(
    () => ({ blocker, owner: blockedOwner, register, discard, keep }),
    [blocker, blockedOwner, register, discard, keep],
  );
  return <Scope.Provider value={scope}>{children}</Scope.Provider>;
}
